import { css } from '@emotion/react'
import { Cell, CellSize, Grid, InfoLabel, Text, VFlow } from 'bold-ui'
import { DataTable, TableColumnConfig } from 'bold-ui/lib/components/Table/DataTable/DataTable'
import { DateTime } from 'components/date'
import { createRender } from 'components/table/render'
import { Fragment } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { formatNumber } from 'util/number'
import {
  HistoricoAntropometriaModel,
  MEDICOES_ANTROPOMETRIA_PAC,
  TipoUnidadeMedicaoForcaMuscular,
} from 'view/atendimentos/detail/historico/model/model-historico'

import HistoricoDetailTable from '../../../table/HistoricoDetailTable'
import HistoricoMedicoesDataTable from './HistoricoMedicoesDataTable'
import HistoricoMedicoesDetailTableRow from './HistoricoMedicoesDetailTableRow'

interface HistoricoMedicoesAntropometriaProps {
  antropometria: HistoricoAntropometriaModel[]
  isAtendimentoObservacao?: boolean
}

export default function HistoricoMedicoesAntropometria(props: Readonly<HistoricoMedicoesAntropometriaProps>) {
  const { antropometria, isAtendimentoObservacao = false } = props

  const hasAntropometriaPac = antropometria.some((medicao) => MEDICOES_ANTROPOMETRIA_PAC.some((it) => medicao[it]))
  const detailTableTitle = hasAntropometriaPac ? 'Antropometria e exame físico' : 'Antropometria'
  const title = isAtendimentoObservacao ? '' : detailTableTitle

  const renderExameFisico = () => {
    return (
      <HistoricoDetailTable title={title}>
        {antropometria.map((antropometria) => (
          <VFlow key={antropometria.id}>
            <Grid>
              {!isAtendimentoObservacao && (
                <Fragment>
                  {renderItem('Peso', 2, antropometria.peso, ' kg')}
                  {renderItem('Altura', 2, antropometria.altura, ' cm')}
                  {renderItem('IMC', 2, antropometria.imc?.toFixed(2), ' Kg/m²')}
                  {renderItem('Per. cefálico', 2, antropometria.perimetroCefalico, ' cm')}
                </Fragment>
              )}
              <HistoricoMedicoesDetailTableRow titulo='Pele' hasItems={!isUndefinedOrNull(antropometria.pele)}>
                <Cell size={12} style={styles.pele}>
                  {antropometria.pele}
                </Cell>
              </HistoricoMedicoesDetailTableRow>

              <HistoricoMedicoesDetailTableRow
                titulo='Olhos'
                hasItems={
                  !isUndefinedOrNull(antropometria.olhoDireito) || !isUndefinedOrNull(antropometria.olhoEsquerdo)
                }
              >
                <Fragment>
                  {renderItem('Olho direito', 6, antropometria.olhoDireito)}
                  {renderItem('Olho esquerdo', 6, antropometria.olhoEsquerdo)}
                </Fragment>
              </HistoricoMedicoesDetailTableRow>
              <HistoricoMedicoesDetailTableRow
                titulo='Ouvidos'
                hasItems={
                  !isUndefinedOrNull(antropometria.ouvidoDireito) || !isUndefinedOrNull(antropometria.ouvidoEsquerdo)
                }
              >
                <Fragment>
                  {renderItem('Ouvido direito', 6, antropometria.ouvidoDireito)}
                  {renderItem('Ouvido esquerdo', 6, antropometria.ouvidoEsquerdo)}
                </Fragment>
              </HistoricoMedicoesDetailTableRow>
              <HistoricoMedicoesDetailTableRow
                titulo='Avaliação de força muscular'
                hasItems={
                  !isUndefinedOrNull(antropometria.valorForcaMuscMaoDireita) ||
                  !isUndefinedOrNull(antropometria.valorForcaMuscMaoEsquerda)
                }
              >
                <Fragment>
                  {renderItem(
                    'Mão direita',
                    6,
                    antropometria.valorForcaMuscMaoDireita,
                    TipoUnidadeMedicaoForcaMuscular[antropometria.unidadeForcaMuscMaoDireita?.enum]
                  )}
                  {renderItem(
                    'Mão esquerda',
                    6,
                    antropometria.valorForcaMuscMaoEsquerda,
                    TipoUnidadeMedicaoForcaMuscular[antropometria.unidadeForcaMuscMaoEsquerda?.enum]
                  )}
                </Fragment>
              </HistoricoMedicoesDetailTableRow>
              {!isAtendimentoObservacao && (
                <HistoricoMedicoesDetailTableRow
                  titulo='Circunferência abdominal'
                  hasItems={!isUndefinedOrNull(antropometria.circunferenciaAbdominal)}
                >
                  <Text
                    style={css`
                      padding: 0rem 1rem 0.5rem 1rem;
                    `}
                  >
                    {antropometria.circunferenciaAbdominal + ' cm'}
                  </Text>
                </HistoricoMedicoesDetailTableRow>
              )}
              <HistoricoMedicoesDetailTableRow
                titulo='Membros inferiores'
                hasItems={
                  !isUndefinedOrNull(antropometria.membroInferiorDireito) ||
                  !isUndefinedOrNull(antropometria.membroInferiorEsquerdo) ||
                  (isAtendimentoObservacao ? false : !isUndefinedOrNull(antropometria.perimetroPanturrilha))
                }
              >
                <Fragment>
                  {renderItem('Membro inferior direito', 6, antropometria.membroInferiorDireito)}
                  {renderItem('Membro inferior esquerdo', 6, antropometria.membroInferiorEsquerdo)}
                  {!isAtendimentoObservacao &&
                    renderItem('Perímetro da panturrilha', 6, antropometria.perimetroPanturrilha, ' cm')}
                </Fragment>
              </HistoricoMedicoesDetailTableRow>
            </Grid>
          </VFlow>
        ))}
      </HistoricoDetailTable>
    )
  }

  return isAtendimentoObservacao ? (
    <VFlow>
      <HistoricoMedicoesDataTable titulo='Antropometria e exame físico'>
        <DataTable<HistoricoAntropometriaModel> columns={antropometriaColumns} rows={antropometria} />
      </HistoricoMedicoesDataTable>

      {hasAntropometriaPac && <VFlow style={styles.observacao}>{renderExameFisico()}</VFlow>}
    </VFlow>
  ) : (
    renderExameFisico()
  )
}

const antropometriaColumns: TableColumnConfig<HistoricoAntropometriaModel>[] = [
  {
    header: 'Data da medição',
    name: 'dataMedicao',
    render: (antropometria) => <DateTime value={antropometria.dataMedicao} format='DD/MM/YYYY' />,
  },
  {
    header: 'Peso (kg)',
    name: 'valopesorPeso',
    render: (antropometria) => createRender(antropometria, 'peso', (value) => formatNumber(value, 2)),
  },
  {
    header: 'Altura (cm)',
    name: 'altura',
    render: (antropometria) => createRender(antropometria, 'altura', (value) => formatNumber(value, 1)),
  },
  {
    header: 'IMC (kg/m²)',
    name: 'imc',
    render: (antropometria) => createRender(antropometria, 'imc', (value) => formatNumber(value, 2)),
  },
  {
    header: 'Per. cefálico (cm)',
    name: 'perimetroCefalico',
    render: (antropometria) => createRender(antropometria, 'perimetroCefalico', (value) => formatNumber(value, 1)),
  },
  {
    header: 'Circ. abdominal (cm)',
    name: 'circunferenciaAbdominal',
    render: (antropometria) =>
      createRender(antropometria, 'circunferenciaAbdominal', (value) => formatNumber(value, 1)),
  },
  {
    header: 'Per. panturrilha (cm)',
    name: 'perimetroPanturrilha',
    render: (antropometria) => createRender(antropometria, 'perimetroPanturrilha', (value) => formatNumber(value, 1)),
  },
]

const renderItem = (titulo: string, size: CellSize, item: string | number | undefined, unidade?: string) => {
  const hasItem = !isUndefinedOrNull(item)
  return (
    hasItem && (
      <Cell size={size}>
        <InfoLabel title={titulo} childStyles={styles.breakWord}>
          {item}
          {unidade}
        </InfoLabel>
      </Cell>
    )
  )
}

const styles = {
  observacao: css`
    margin-top: -1rem;
  `,
  pele: css`
    padding-top: 0rem;
    word-break: break-word;
  `,
  breakWord: css`
    word-break: break-word;
  `,
}
